import React from 'react';
import { useImmerReducer } from 'use-immer';
import Autocomplete from '../components/Autocomplete';
import {
	Box,
	HStack,
	Flex,
	Input,
	Switch,
	Heading,
	Text,
	Textarea,
	Button,
	Accordion,
	NumberInput,
	CloseButton,
	Dialog,
	Portal,
	Field
} from '@chakra-ui/react';
import theme from '../theme';
import { roles } from '../lib/generics';
import SignaturePad from '../components/SignaturePad';
import MultiRef from 'react-multi-ref';

const getCrewMembers = ({ employee_crew_members = [], non_employee_crew_members = [], crew_members = [], crew = [] }) =>
	[...employee_crew_members, ...non_employee_crew_members, ...crew_members]
		.filter(
			cm =>
				cm.employee_crew_member_name ||
				cm.non_employee_crew_member_name ||
				cm.crew_member_name ||
				cm.employee_crew_member_role ||
				cm.non_employee_crew_member_role ||
				cm.crew_member_role
		)
		.map(cm => {
			cm.name = cm.employee_crew_member_name || cm.non_employee_crew_member_name || cm.crew_member_name;
			cm.role = cm.employee_crew_member_role || cm.non_employee_crew_member_role || cm.crew_member_role;

			const prevCm = crew.find(({ name }) => name.toLowerCase() === cm.name.toLowerCase());

			if (prevCm) {
				cm.signature = prevCm.signature ?? null;
				cm.physically_fit = prevCm.physically_fit ?? false;
				cm.ready_to_dive = prevCm.ready_to_dive ?? false;
			}

			return { ...cm };
		});

function formReducer(draft, action) {
	switch (action.type) {
		case 'update_field_value': {
			const { section, field, value } = action.payload;
			draft.fields[section][field] = value;

			break;
		}
		case 'update_crew_member': {
			const { field, index, value } = action.payload;
			draft.fields.crew[index][field] = value;

			break;
		}
		case 'add_orange_hat': {
			draft.fields.team_assessment.orange_hat_list.push({
				team_member: draft.fields.team_assessment.team_member,
				mentor: draft.fields.team_assessment.mentor
			});

			draft.fields.team_assessment.team_member = '';
			draft.fields.team_assessment.mentor = '';

			break;
		}
		case 'remove_orange_hat': {
			const { index } = action.payload;
			draft.fields.team_assessment.orange_hat_list.splice(index, 1);

			break;
		}
		case 'add_meeting_attendee': {
			draft.fields.attendees.push({
				name: '',
				signature: null,
				company_name: '',
				physically_fit: false,
				ready_to_dive: false
			});

			break;
		}
		case 'update_meeting_attendee': {
			const { field, index, value } = action.payload;
			draft.fields.attendees[index][field] = value;

			break;
		}
		case 'remove_meeting_attendee': {
			const { index } = action.payload;
			draft.fields.attendees.splice(index, 1);

			break;
		}
		case 'save_signature': {
			const { cmIndex, signatureData, crew = true } = action.payload;
			if (!crew) {
				const attendee = draft.fields.attendees[cmIndex];

				if (attendee) {
					attendee.signature = signatureData;
				}
			} else {
				const crewMember = draft.fields.crew[cmIndex];

				if (crewMember) {
					crewMember.signature = signatureData;
				}
			}

			break;
		}
		default:
			throw new Error();
	}
}

export default ({ formData, handoffData, edit, saveForm, currentForm, isSubmitted }) => {
	const [state, dispatch] = useImmerReducer(formReducer, {
		fields: {
			...formData,
			crew: getCrewMembers({ ...handoffData, crew: formData.crew }),
			hazards: {
				...formData.hazards,
				known_hazards: formData?.hazards?.known_hazards || handoffData.known_hazards || ''
			},
			emergency_procedures: {
				...formData.emergency_procedures,
				emergency_contacts: handoffData.emergency_contacts?.filter(
					emergency_contact =>
						emergency_contact.emergency_contact_name || emergency_contact.emergency_contact_phone_number
				),
				hospitals: handoffData.hospitals?.filter(
					hospital => hospital.name || hospital.hospital_phone_number || hospital.location
				),
				hospital_name: handoffData.hospital_name,
				hospital_phone_number: handoffData.hospital_phone_number,
				hospital_location: handoffData.hospital_location
			}
		}
	});

	const [removeOrangeHatsDialogOpen, setRemoveOrangeHatsDialogOpen] = React.useState(null);
	const [removeMeetingAttendeeDialogOpen, setRemoveMeetingAttendeeDialogOpen] = React.useState(null);
	const [removeTeamMemberAutocompleteDialogOpen, setRemoveTeamMemberAutocompleteDialogOpen] = React.useState(false);
	const [removeMentorAutocompleteDialogOpen, setRemoveMentorAutocompleteDialogOpen] = React.useState(false);

	const [orangeHatRefs] = React.useState(() => new MultiRef());
	const [meetingAttendeeRefs] = React.useState(() => new MultiRef());

	React.useEffect(() => {
		if (!state.fields.team_assessment.orange_hat_list) {
			dispatch({
				type: 'update_field_value',
				payload: {
					section: 'team_assessment',
					field: 'orange_hat_list',
					value: [
						state.fields.team_assessment.orange_hat_mentor
							? {
									team_member: '',
									mentor: state.fields.team_assessment.orange_hat_mentor
								}
							: null
					].filter(Boolean)
				}
			});
		}
	}, []);

	React.useEffect(() => {
		saveForm({
			...currentForm,
			form_data: state.fields
		});
	}, [state.fields]);

	const reqs = {
		dive_mode_selection:
			state.fields.dive_mode.surface_supplied_air ||
			state.fields.dive_mode.scuba ||
			state.fields.dive_mode.wading,
		protective_equipment:
			state.fields.protective_equipment.gloves ||
			state.fields.protective_equipment.hard_hat ||
			state.fields.protective_equipment.hearing_protection ||
			state.fields.protective_equipment.safety_glasses ||
			state.fields.protective_equipment.safety_vests ||
			state.fields.protective_equipment.steel_toe_boots ||
			state.fields.protective_equipment.other !== ''
	};

	return (
		<>
			<Accordion.Root multiple collapsible p="8px" defaultValue={[0, 1, 2, 3, 4, 5]}>
				<Accordion.Item value={0} mb={4} borderBottomWidth="1px">
					<Accordion.ItemTrigger>
						<Accordion.ItemIndicator />
						<Text fontSize={20} fontWeight="bold" color={theme.colors.blue[700]}>
							<Text fontSize={20} fontWeight="bold" color={theme.colors.blue[700]}>
								Dive Mode
							</Text>
						</Text>
					</Accordion.ItemTrigger>
					<Accordion.ItemContent p={0}>
						<Accordion.ItemBody>
							<Flex>
								<HStack
									w="100%"
									p="8px"
									m="8px"
									bgColor={theme.colors.gray[500]}
									borderRadius="base"
									borderBottom={`2px solid ${!reqs.dive_mode_selection ? 'red' : 'transparent'}`}
								>
									<Switch.Root
										colorPalette="blue"
										size="lg"
										checked={state.fields.dive_mode.surface_supplied_air}
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'dive_mode',
													field: 'surface_supplied_air',
													value: !state.fields.dive_mode.surface_supplied_air
												}
											});
										}}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text as="label" fontSize={16} pl="4px">
										Surface Supplied Air (SSA)
									</Text>
								</HStack>
								<HStack
									w="100%"
									p="8px"
									m="8px"
									bgColor={theme.colors.gray[500]}
									borderRadius="base"
									borderBottom={`2px solid ${!reqs.dive_mode_selection ? 'red' : 'transparent'}`}
								>
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'dive_mode',
													field: 'scuba',
													value: !state.fields.dive_mode.scuba
												}
											});
										}}
										checked={state.fields.dive_mode.scuba}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text as="label" fontSize={16} pl="4px">
										SCUBA
									</Text>
								</HStack>
								<HStack
									w="100%"
									p="8px"
									m="8px"
									bgColor={theme.colors.gray[500]}
									borderRadius="base"
									borderBottom={`2px solid ${!reqs.dive_mode_selection ? 'red' : 'transparent'}`}
								>
									<Switch.Root
										colorPalette="blue"
										size="lg"
										checked={state.fields.dive_mode.wading}
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'dive_mode',
													field: 'wading',
													value: !state.fields.dive_mode.wading
												}
											});
										}}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text as="label" fontSize={16} pl="4px">
										Wading
									</Text>
								</HStack>
							</Flex>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'dive_mode',
													field: 'dive_plan',
													value: !state.fields.dive_mode.dive_plan
												}
											});
										}}
										checked={state.fields.dive_mode.dive_plan}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text as="label" fontSize={16} pl="4px">
										Dive Plan
									</Text>
								</HStack>
							</Flex>
							<Flex>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text as="label" fontSize={16} pb="4px">
										Objective of Dive
									</Text>
									<Field.Root invalid={!state.fields.dive_mode.objective_of_dive}>
										<Textarea
											fontSize={16}
											bgColor={theme.colors.white}
											value={state.fields.dive_mode.objective_of_dive}
											onChange={({ target }) => {
												dispatch({
													type: 'update_field_value',
													payload: {
														section: 'dive_mode',
														field: 'objective_of_dive',
														value: target.value
													}
												});
											}}
											disabled={isSubmitted && !edit}
											_disabled={{
												opacity: 1,
												bgColor: theme.colors.gray[400],
												color: theme.colors.black
											}}
										/>
									</Field.Root>
								</Box>
							</Flex>
						</Accordion.ItemBody>
					</Accordion.ItemContent>
				</Accordion.Item>
				<Accordion.Item value={1} mb={4} borderBottomWidth="1px">
					<Accordion.ItemTrigger>
						<Accordion.ItemIndicator />
						<Text fontSize={20} fontWeight="bold" color={theme.colors.blue[700]}>
							Team
						</Text>
					</Accordion.ItemTrigger>
					<Accordion.ItemContent p={0}>
						<Accordion.ItemBody>
							{state.fields.crew?.map((cm, cmIndex) => (
								<Flex key={`${cm.name}_${cmIndex}`}>
									<Box w="100%" m="8px" p="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
										<Text as="label" fontSize={16} pb="4px">
											Name
										</Text>
										<Text fontSize={16}>{cm.name}</Text>
									</Box>
									<Box w="100%" m="8px" p="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
										<Text as="label" fontSize={16} pb="4px">
											Role
										</Text>
										<Text fontSize={16}>{roles[cm.role]}</Text>
									</Box>
								</Flex>
							))}
							<Heading
								as="label"
								fontSize={24}
								pt="24px"
								pb="12px"
								color={theme.colors.blue[700]}
								px="8px"
							>
								Supervisor Assessment of Team Members
							</Heading>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'team_assessment',
													field: 'physically_fit',
													value: !state.fields.team_assessment.physically_fit
												}
											});
										}}
										checked={state.fields.team_assessment.physically_fit}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text as="label" fontSize={16} pl="4px">
										All team members are physically fit
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'team_assessment',
													field: 'illnesses',
													value: !state.fields.team_assessment.illnesses
												}
											});
										}}
										checked={state.fields.team_assessment.illnesses}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text as="label" fontSize={16} pl="4px">
										Have any team members had an illness within the last 5-7 days?
									</Text>
								</HStack>
							</Flex>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'team_assessment',
													field: 'fever_temperature_chills',
													value: !state.fields.team_assessment.fever_temperature_chills
												}
											});
										}}
										checked={state.fields.team_assessment.fever_temperature_chills}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text as="label" fontSize={16} pl="4px">
										Have any team members had a fever/temperature/chills in the last 24-48 hours?
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'team_assessment',
													field: 'taking_medications',
													value: !state.fields.team_assessment.taking_medications
												}
											});
										}}
										checked={state.fields.team_assessment.taking_medications}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text as="label" fontSize={16} pl="4px">
										Are any team members currently taking any medications?
									</Text>
								</HStack>
							</Flex>
							<Flex align="flex-start">
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'team_assessment',
													field: 'ready_to_dive',
													value: !state.fields.team_assessment.ready_to_dive
												}
											});
										}}
										checked={state.fields.team_assessment.ready_to_dive}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text as="label" fontSize={16} pl="4px">
										Ready To Dive
									</Text>
								</HStack>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text as="label" fontSize={16} pb="4px">
										Please include any relevant information to the questions above.
									</Text>
									<Textarea
										fontSize={16}
										bgColor={theme.colors.white}
										value={state.fields.team_assessment.relevant_information}
										onChange={({ target }) => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'team_assessment',
													field: 'relevant_information',
													value: target.value
												}
											});
										}}
										disabled={isSubmitted && !edit}
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
									/>
								</Box>
							</Flex>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() =>
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'team_assessment',
													field: 'orange_hats',
													value: !state.fields.team_assessment.orange_hats
												}
											})
										}
										checked={state.fields.team_assessment.orange_hats}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text as="label" fontSize={16} pl="4px">
										Do you have any Orange Hats on your Team?
									</Text>
								</HStack>
							</Flex>
							{state.fields.team_assessment.orange_hats ? (
								<>
									<Flex
										direction="column"
										m="8px"
										bgColor={theme.colors.gray[500]}
										borderRadius="base"
									>
										<Flex>
											<Text w="100%" as="label" fontSize={16} p="8px">
												Team Member Name
											</Text>
											<Text w="100%" as="label" fontSize={16} p="8px">
												Who is the onsite mentor assigned to them?
											</Text>
										</Flex>
										{state.fields.team_assessment.orange_hat_list?.map((orange_hat, index) => (
											<Flex key={index} align="center">
												<Text w="100%" fontSize={16} p="8px">
													{orange_hat.team_member}
												</Text>
												<Flex w="100%" alignItems="center">
													<Text w="100%" fontSize={16} p="8px">
														{orange_hat.mentor}
													</Text>
													{(!isSubmitted || edit) && (
														<>
															<CloseButton
																size="md"
																m="8px"
																color={theme.colors.white}
																bgColor={theme.colors.red[500]}
																_hover={{ background: theme.colors.red[600] }}
																disabled={isSubmitted && !edit}
																onClick={() => setRemoveOrangeHatsDialogOpen(index)}
															/>
															<Dialog.Root
																role="alertdialog"
																initialFocusEl={orangeHatRefs.map.get(index)}
																onOpenChange={({ open }) => {
																	if (!open) {
																		setRemoveOrangeHatsDialogOpen(null);
																	}
																}}
																open={removeOrangeHatsDialogOpen === index}
																placement="center"
															>
																<Portal>
																	<Dialog.Backdrop />
																	<Dialog.Positioner>
																		<Dialog.Content opacity="1 !important">
																			<Dialog.Header>
																				<Dialog.Title
																					fontSize="lg"
																					fontWeight="bold"
																				>
																					Confirm Delete
																				</Dialog.Title>
																			</Dialog.Header>
																			<Dialog.Body>
																				<p>
																					Are you sure you want to remove this
																					item?
																				</p>
																			</Dialog.Body>
																			<Dialog.Footer>
																				<Dialog.ActionTrigger asChild>
																					<Button
																						size="lg"
																						ref={orangeHatRefs.ref(index)}
																						onClick={() =>
																							setRemoveOrangeHatsDialogOpen(
																								null
																							)
																						}
																					>
																						Cancel
																					</Button>
																				</Dialog.ActionTrigger>
																				<Button
																					colorPalette="red"
																					size="lg"
																					ml={3}
																					bg={theme.colors.red[500]}
																					_hover={{
																						background:
																							theme.colors.red[600]
																					}}
																					color={theme.colors.white}
																					onClick={() => {
																						setRemoveOrangeHatsDialogOpen(
																							null
																						);
																						dispatch({
																							type: 'remove_orange_hat',
																							payload: { index: index }
																						});
																					}}
																				>
																					Delete Item
																				</Button>
																			</Dialog.Footer>
																			<Dialog.CloseTrigger asChild>
																				<CloseButton size="sm" />
																			</Dialog.CloseTrigger>
																		</Dialog.Content>
																	</Dialog.Positioner>
																</Portal>
															</Dialog.Root>
														</>
													)}
												</Flex>
											</Flex>
										))}
									</Flex>
									<Flex align="flex-end" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
										<Flex w="100%" p="8px" flexDirection="column" justifyContent="space-between">
											<Dialog.Root
												role="alertdialog"
												closeOnEscape={false}
												closeOnInteractOutside={false}
												onOpenChange={({ open }) => {
													if (!open) {
														setRemoveTeamMemberAutocompleteDialogOpen(false);
													}
												}}
												open={removeTeamMemberAutocompleteDialogOpen}
												placement="center"
											>
												<Portal>
													<Dialog.Backdrop />
													<Dialog.Positioner>
														<Dialog.Content opacity="1 !important">
															<Dialog.Header>
																<Dialog.Title fontSize="lg" fontWeight="bold">
																	Could not find an employee match.
																</Dialog.Title>
															</Dialog.Header>
															<Dialog.Body>
																<p>
																	Please check that the name is spelled correctly. We
																	suggest you select a match from the autocomplete
																	list below the input. The employee will be removed,
																	please try adding again with a correct selection.
																</p>
															</Dialog.Body>
															<Dialog.Footer>
																<Button
																	colorPalette="red"
																	size="lg"
																	ml={3}
																	bg={theme.colors.red[500]}
																	_hover={{ background: theme.colors.red[600] }}
																	color={theme.colors.white}
																	onClick={() => {
																		setRemoveTeamMemberAutocompleteDialogOpen(
																			false
																		);
																		dispatch({
																			type: 'update_field_value',
																			payload: {
																				section: 'team_assessment',
																				field: 'team_member',
																				value: ''
																			}
																		});
																	}}
																>
																	Remove Item
																</Button>
															</Dialog.Footer>
														</Dialog.Content>
													</Dialog.Positioner>
												</Portal>
											</Dialog.Root>
											<Text as="label" fontSize={16} pb="4px">
												Team Member Name
											</Text>
											<Field.Root
												invalid={
													!state.fields.team_assessment.orange_hat_list?.length ||
													(state.fields.team_assessment.mentor &&
														(!state.fields.team_assessment.team_member ||
															state.fields.team_assessment.team_member === ''))
												}
											>
												<Autocomplete
													disabled={isSubmitted && !edit}
													dataList={window.employeeList}
													fieldName="name"
													value={state.fields.team_assessment.team_member}
													onChange={value => {
														dispatch({
															type: 'update_field_value',
															payload: {
																section: 'team_assessment',
																field: 'team_member',
																value
															}
														});
													}}
													onBlur={() =>
														setRemoveTeamMemberAutocompleteDialogOpen(
															state.fields.team_assessment.team_member &&
																!window.employeeList.find(
																	({ name }) =>
																		name ===
																		state.fields.team_assessment.team_member
																)
														)
													}
													onSelected={value => {
														dispatch({
															type: 'update_field_value',
															payload: {
																section: 'team_assessment',
																field: 'team_member',
																value
															}
														});
													}}
												/>
											</Field.Root>
										</Flex>
										<Flex w="100%" alignItems="flex-end">
											<Flex w="100%" p="8px" flexDirection="column">
												<Dialog.Root
													role="alertdialog"
													closeOnEscape={false}
													closeOnInteractOutside={false}
													onOpenChange={({ open }) => {
														if (!open) {
															setRemoveMentorAutocompleteDialogOpen(false);
														}
													}}
													open={removeMentorAutocompleteDialogOpen}
													placement="center"
												>
													<Portal>
														<Dialog.Backdrop />
														<Dialog.Positioner>
															<Dialog.Content opacity="1 !important">
																<Dialog.Header>
																	<Dialog.Title fontSize="lg" fontWeight="bold">
																		Could not find an employee match.
																	</Dialog.Title>
																</Dialog.Header>
																<Dialog.Body>
																	<p>
																		Please check that the name is spelled correctly.
																		We suggest you select a match from the
																		autocomplete list below the input. The employee
																		will be removed, please try adding again with a
																		correct selection.
																	</p>
																</Dialog.Body>
																<Dialog.Footer>
																	<Button
																		colorPalette="red"
																		size="lg"
																		ml={3}
																		bg={theme.colors.red[500]}
																		_hover={{ background: theme.colors.red[600] }}
																		color={theme.colors.white}
																		onClick={() => {
																			setRemoveMentorAutocompleteDialogOpen(
																				false
																			);
																			dispatch({
																				type: 'update_field_value',
																				payload: {
																					section: 'team_assessment',
																					field: 'mentor',
																					value: ''
																				}
																			});
																		}}
																	>
																		Remove Item
																	</Button>
																</Dialog.Footer>
															</Dialog.Content>
														</Dialog.Positioner>
													</Portal>
												</Dialog.Root>
												<Text as="label" fontSize={16} pb="4px">
													Who is the onsite mentor assigned to them?
												</Text>
												<Field.Root
													invalid={
														!state.fields.team_assessment.orange_hat_list?.length ||
														(state.fields.team_assessment.team_member &&
															(!state.fields.team_assessment.mentor ||
																state.fields.team_assessment.mentor === ''))
													}
												>
													<Autocomplete
														disabled={isSubmitted && !edit}
														dataList={window.employeeList}
														fieldName="name"
														value={state.fields.team_assessment.mentor}
														onChange={value => {
															dispatch({
																type: 'update_field_value',
																payload: {
																	section: 'team_assessment',
																	field: 'mentor',
																	value
																}
															});
														}}
														onBlur={() =>
															setRemoveMentorAutocompleteDialogOpen(
																state.fields.team_assessment.mentor &&
																	!window.employeeList.find(
																		({ name }) =>
																			name === state.fields.team_assessment.mentor
																	)
															)
														}
														onSelected={value => {
															dispatch({
																type: 'update_field_value',
																payload: {
																	section: 'team_assessment',
																	field: 'mentor',
																	value
																}
															});
														}}
													/>
												</Field.Root>
											</Flex>
											<Button
												size="lg"
												m="8px"
												color={theme.colors.white}
												bg={theme.colors.blue[700]}
												_hover={{ backgroundColor: theme.colors.blue[800] }}
												onClick={() => {
													if (
														state.fields.team_assessment.team_member &&
														state.fields.team_assessment.mentor
													) {
														dispatch({ type: 'add_orange_hat' });
													}
												}}
												disabled={isSubmitted && !edit}
											>
												Add
											</Button>
										</Flex>
									</Flex>
								</>
							) : null}
						</Accordion.ItemBody>
					</Accordion.ItemContent>
				</Accordion.Item>
				<Accordion.Item value={2} mb={4} borderBottomWidth="1px">
					<Accordion.ItemTrigger>
						<Accordion.ItemIndicator />
						<Text fontSize={20} fontWeight="bold" color={theme.colors.blue[700]}>
							Conditions
						</Text>
					</Accordion.ItemTrigger>
					<Accordion.ItemContent p={0}>
						<Accordion.ItemBody>
							<Flex align="flex-start">
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text as="label" fontSize={16} pb="4px">
										Weather
									</Text>
									<Textarea
										fontSize={16}
										bgColor={theme.colors.white}
										value={state.fields.conditions.weather}
										onChange={({ target }) => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'conditions',
													field: 'weather',
													value: target.value
												}
											});
										}}
										disabled={isSubmitted && !edit}
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
									/>
								</Box>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text as="label" fontSize={16} pb="4px">
										Air Temp (°F)
									</Text>
									<NumberInput.Root
										value={state.fields.conditions.weather_temp}
										disabled={isSubmitted && !edit}
										onValueChange={e => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'conditions',
													field: 'weather_temp',
													value: e.value
												}
											});
										}}
									>
										<NumberInput.Input
											fontSize={16}
											bgColor={theme.colors.white}
											_disabled={{
												opacity: 1,
												bgColor: theme.colors.gray[400],
												color: theme.colors.black
											}}
										/>
									</NumberInput.Root>
								</Box>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text as="label" fontSize={16} pb="4px">
										Water Temp (°F)
									</Text>
									<NumberInput.Root
										value={state.fields.conditions.water_temp}
										disabled={isSubmitted && !edit}
										onValueChange={e => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'conditions',
													field: 'water_temp',
													value: e.value
												}
											});
										}}
									>
										<NumberInput.Input
											fontSize={16}
											bgColor={theme.colors.white}
											_disabled={{
												opacity: 1,
												bgColor: theme.colors.gray[400],
												color: theme.colors.black
											}}
										/>
									</NumberInput.Root>
								</Box>
							</Flex>
							<Flex>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text as="label" fontSize={16} pb="4px">
										Water Current (KT)
									</Text>
									<NumberInput.Root
										value={state.fields.conditions.water_current}
										disabled={isSubmitted && !edit}
										onValueChange={e => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'conditions',
													field: 'water_current',
													value: e.value
												}
											});
										}}
									>
										<NumberInput.Input
											fontSize={16}
											bgColor={theme.colors.white}
											_disabled={{
												opacity: 1,
												bgColor: theme.colors.gray[400],
												color: theme.colors.black
											}}
										/>
									</NumberInput.Root>
								</Box>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text as="label" fontSize={16} pb="4px">
										Maximum Depth (ft)
									</Text>
									<NumberInput.Root
										value={state.fields.conditions.maximum_depth}
										disabled={isSubmitted && !edit}
										onValueChange={e => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'conditions',
													field: 'maximum_depth',
													value: e.value
												}
											});
										}}
									>
										<NumberInput.Input
											fontSize={16}
											bgColor={theme.colors.white}
											_disabled={{
												opacity: 1,
												bgColor: theme.colors.gray[400],
												color: theme.colors.black
											}}
										/>
									</NumberInput.Root>
								</Box>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text as="label" fontSize={16} pb="4px">
										Visibility (FT)
									</Text>
									<NumberInput.Root
										value={state.fields.conditions.visibility}
										disabled={isSubmitted && !edit}
										onValueChange={e => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'conditions',
													field: 'visibility',
													value: e.value
												}
											});
										}}
									>
										<NumberInput.Input
											fontSize={16}
											bgColor={theme.colors.white}
											_disabled={{
												opacity: 1,
												bgColor: theme.colors.gray[400],
												color: theme.colors.black
											}}
										/>
									</NumberInput.Root>
								</Box>
							</Flex>
						</Accordion.ItemBody>
					</Accordion.ItemContent>
				</Accordion.Item>
				<Accordion.Item value={3} mb={4} borderBottomWidth="1px">
					<Accordion.ItemTrigger>
						<Accordion.ItemIndicator />
						<Text fontSize={20} fontWeight="bold" color={theme.colors.blue[700]}>
							Job Hazard Analysis
						</Text>
					</Accordion.ItemTrigger>
					<Accordion.ItemContent p={0}>
						<Accordion.ItemBody>
							<Flex>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text as="label" fontSize={16} pb="4px">
										Known Hazards
									</Text>
									<Field.Root invalid={!state.fields.hazards.known_hazards}>
										<Textarea
											fontSize={16}
											bgColor={theme.colors.white}
											value={state.fields.hazards.known_hazards}
											onChange={({ target }) => {
												dispatch({
													type: 'update_field_value',
													payload: {
														section: 'hazards',
														field: 'known_hazards',
														value: target.value
													}
												});
											}}
											disabled={isSubmitted && !edit}
											_disabled={{
												opacity: 1,
												bgColor: theme.colors.gray[400],
												color: theme.colors.black
											}}
										/>
									</Field.Root>
								</Box>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text as="label" fontSize={16} pb="4px">
										Additional Hazards
									</Text>
									<Textarea
										fontSize={16}
										bgColor={theme.colors.white}
										value={state.fields.hazards.other_hazards}
										onChange={({ target }) => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'hazards',
													field: 'other_hazards',
													value: target.value
												}
											});
										}}
										disabled={isSubmitted && !edit}
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
									/>
								</Box>
							</Flex>
							<Heading
								fontSize={24}
								as="label"
								pt="24px"
								pb="12px"
								color={theme.colors.blue[700]}
								px="8px"
							>
								Thermal Protection
							</Heading>
							<Flex>
								<Flex />
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'thermal_protection',
													field: 'wet_suit',
													value: !state.fields.thermal_protection.wet_suit
												}
											});
										}}
										checked={state.fields.thermal_protection.wet_suit}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text as="label" fontSize={16} pl="4px">
										Wet Suit
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text as="label" fontSize={16} pl="4px">
										Dry Suit
									</Text>
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'thermal_protection',
													field: 'dry_suit',
													value: !state.fields.thermal_protection.dry_suit
												}
											});
										}}
										checked={state.fields.thermal_protection.dry_suit}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
								</HStack>
							</Flex>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'thermal_protection',
													field: 'hot_water',
													value: !state.fields.thermal_protection.hot_water
												}
											});
										}}
										checked={state.fields.thermal_protection.hot_water}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text as="label" fontSize={16} pl="4px">
										Hot Water
									</Text>
								</HStack>
								{state.fields.thermal_protection.hot_water && (
									<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
										<Text as="label" fontSize={16} pb="4px">
											Hot Water Temp Setting (°F)
										</Text>
										<NumberInput.Root
											value={state.fields.thermal_protection.temp_setting}
											disabled={isSubmitted && !edit}
											onValueChange={e => {
												dispatch({
													type: 'update_field_value',
													payload: {
														section: 'thermal_protection',
														field: 'temp_setting',
														value: e.value
													}
												});
											}}
										>
											<NumberInput.Input
												fontSize={16}
												bgColor={theme.colors.white}
												_disabled={{
													opacity: 1,
													bgColor: theme.colors.gray[400],
													color: theme.colors.black
												}}
											/>
										</NumberInput.Root>
									</Box>
								)}
							</Flex>
							<Flex>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text as="label" fontSize={16} pb="4px">
										Dive Modifications
									</Text>
									<Textarea
										fontSize={16}
										bgColor={theme.colors.white}
										value={state.fields.thermal_protection.dive_modifications}
										onChange={({ target }) => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'thermal_protection',
													field: 'dive_modifications',
													value: target.value
												}
											});
										}}
										disabled={isSubmitted && !edit}
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
									/>
								</Box>
							</Flex>
							<Heading
								as="label"
								fontSize={24}
								pt="24px"
								pb="12px"
								color={theme.colors.blue[700]}
								px="8px"
							>
								Topside Personal Protective Equipment
							</Heading>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'protective_equipment',
													field: 'hard_hat',
													value: !state.fields.protective_equipment.hard_hat
												}
											});
										}}
										checked={state.fields.protective_equipment.hard_hat}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text as="label" fontSize={16} pl="4px">
										Hard Hat
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'protective_equipment',
													field: 'safety_glasses',
													value: !state.fields.protective_equipment.safety_glasses
												}
											});
										}}
										checked={state.fields.protective_equipment.safety_glasses}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text as="label" fontSize={16} pl="4px">
										Safety Glasses
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'protective_equipment',
													field: 'steel_toe_boots',
													value: !state.fields.protective_equipment.steel_toe_boots
												}
											});
										}}
										checked={state.fields.protective_equipment.steel_toe_boots}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text as="label" fontSize={16} pl="4px">
										Steel Toe Boots
									</Text>
								</HStack>
							</Flex>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'protective_equipment',
													field: 'gloves',
													value: !state.fields.protective_equipment.gloves
												}
											});
										}}
										checked={state.fields.protective_equipment.gloves}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text as="label" fontSize={16} pl="4px">
										Gloves
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'protective_equipment',
													field: 'safety_vests',
													value: !state.fields.protective_equipment.safety_vests
												}
											});
										}}
										checked={state.fields.protective_equipment.safety_vests}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text as="label" fontSize={16} pl="4px">
										High Vis Vests
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'protective_equipment',
													field: 'hearing_protection',
													value: !state.fields.protective_equipment.hearing_protection
												}
											});
										}}
										checked={state.fields.protective_equipment.hearing_protection}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text as="label" fontSize={16} pl="4px">
										Hearing Protection
									</Text>
								</HStack>
							</Flex>
							<Flex align="flex-start">
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'protective_equipment',
													field: 'pfd',
													value: !state.fields.protective_equipment?.pfd
												}
											});
										}}
										checked={state.fields.protective_equipment?.pfd || false}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text as="label" fontSize={16} pl="4px">
										PFD
									</Text>
								</HStack>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text as="label" fontSize={16} pb="4px">
										Other
									</Text>
									<Textarea
										fontSize={16}
										bgColor={theme.colors.white}
										value={state.fields.protective_equipment.other}
										onChange={({ target }) => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'protective_equipment',
													field: 'other',
													value: target.value
												}
											});
										}}
										disabled={isSubmitted && !edit}
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
									/>
								</Box>
							</Flex>
							<Heading
								as="label"
								fontSize={24}
								pt="24px"
								pb="12px"
								color={theme.colors.blue[700]}
								px="8px"
							>
								Topics to Discuss
							</Heading>
							<Flex>
								<HStack
									w="100%"
									p="8px"
									m="8px"
									bgColor={theme.colors.gray[500]}
									borderRadius="base"
									borderBottom={`2px solid ${
										!state.fields.hazards.safety_observations_discussed ? 'red' : 'transparent'
									}`}
								>
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'hazards',
													field: 'safety_observations_discussed',
													value: !state.fields.hazards.safety_observations_discussed
												}
											});
										}}
										checked={state.fields.hazards.safety_observations_discussed}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text as="label" fontSize={16} pl="4px">
										Safety Observations Discussed
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'topics',
													field: 'loss_of_air',
													value: !state.fields.topics.loss_of_air
												}
											});
										}}
										checked={state.fields.topics.loss_of_air}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text as="label" fontSize={16} pl="4px">
										Loss Of Air
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'topics',
													field: 'confined_space_entry',
													value: !state.fields.topics?.confined_space_entry
												}
											});
										}}
										checked={state.fields.topics?.confined_space_entry}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text as="label" fontSize={16} pl="4px">
										Confined Space Entry
									</Text>
								</HStack>
							</Flex>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'topics',
													field: 'emergency_action_plan',
													value: !state.fields.topics.emergency_action_plan
												}
											});
										}}
										checked={state.fields.topics.emergency_action_plan}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text as="label" fontSize={16} pl="4px">
										Emergency Action Plan
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'topics',
													field: 'loss_of_communication',
													value: !state.fields.topics.loss_of_communication
												}
											});
										}}
										checked={state.fields.topics.loss_of_communication}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text as="label" fontSize={16} pl="4px">
										Loss of Communication
									</Text>
								</HStack>
							</Flex>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'topics',
													field: 'pump_suction',
													value: !state.fields.topics.pump_suction
												}
											});
										}}
										checked={state.fields.topics.pump_suction}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text as="label" fontSize={16} pl="4px">
										Pump Suction
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'topics',
													field: 'break_away_from_moored_station',
													value: !state.fields.topics.break_away_from_moored_station
												}
											});
										}}
										checked={state.fields.topics.break_away_from_moored_station}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text as="label" fontSize={16} pl="4px">
										Break Away From Moored Station
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'topics',
													field: 'entrapment_entanglement',
													value: !state.fields.topics.entrapment_entanglement
												}
											});
										}}
										checked={state.fields.topics.entrapment_entanglement}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text as="label" fontSize={16} pl="4px">
										Entrapment/Entanglement
									</Text>
								</HStack>
							</Flex>
							<Flex align="flex-start">
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'topics',
													field: 'underwater_debris',
													value: !state.fields.topics.underwater_debris
												}
											});
										}}
										checked={state.fields.topics.underwater_debris}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text as="label" fontSize={16} pl="4px">
										Underwater Debris
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'topics',
													field: 'valves_pipes',
													value: !state.fields.topics.valves_pipes
												}
											});
										}}
										checked={state.fields.topics.valves_pipes}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text as="label" fontSize={16} pl="4px">
										Valves/Pipes
									</Text>
								</HStack>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text as="label" fontSize={16} pb="4px">
										Tools
									</Text>
									<Textarea
										fontSize={16}
										bgColor={theme.colors.white}
										value={state.fields.topics.tools}
										onChange={({ target }) => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'topics',
													field: 'tools',
													value: target.value
												}
											});
										}}
										disabled={isSubmitted && !edit}
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
									/>
								</Box>
							</Flex>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'hazards',
													field: 'slips_trips_falls',
													value: !state.fields.hazards.slips_trips_falls
												}
											});
										}}
										checked={state.fields.hazards.slips_trips_falls}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text as="label" fontSize={16} pl="4px">
										Slips, Trips, and Falls
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'hazards',
													field: 'overhead',
													value: !state.fields.hazards.overhead
												}
											});
										}}
										checked={state.fields.hazards.overhead}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text as="label" fontSize={16} pl="4px">
										Overhead
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'hazards',
													field: 'burning_welding',
													value: !state.fields.hazards.burning_welding
												}
											});
										}}
										checked={state.fields.hazards.burning_welding}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text as="label" fontSize={16} pl="4px">
										Burning/Welding
									</Text>
								</HStack>
							</Flex>
							<Flex>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text as="label" fontSize={16} pb="4px">
										Other
									</Text>
									<Textarea
										fontSize={16}
										bgColor={theme.colors.white}
										value={state.fields.topics.other}
										onChange={({ target }) => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'topics',
													field: 'other',
													value: target.value
												}
											});
										}}
										disabled={isSubmitted && !edit}
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
									/>
								</Box>
							</Flex>
						</Accordion.ItemBody>
					</Accordion.ItemContent>
				</Accordion.Item>
				<Accordion.Item value={4} mb={4} borderBottomWidth="1px">
					<Accordion.ItemTrigger>
						<Accordion.ItemIndicator />
						<Text fontSize={20} fontWeight="bold" color={theme.colors.blue[700]}>
							<Text fontSize={20} fontWeight="bold" color={theme.colors.blue[700]}>
								Emergency Procedures
							</Text>
						</Text>
					</Accordion.ItemTrigger>
					<Accordion.ItemContent p={0}>
						<Accordion.ItemBody>
							<Flex style={{ marginTop: 24 }}>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'emergency_procedures',
													field: 'first_aid_kit',
													value: !state.fields.emergency_procedures.first_aid_kit
												}
											});
										}}
										checked={state.fields.emergency_procedures.first_aid_kit}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text as="label" fontSize={16} pl="4px">
										First Aid Kit
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'emergency_procedures',
													field: 'fire_extinguishers',
													value: !state.fields.emergency_procedures.fire_extinguishers
												}
											});
										}}
										checked={state.fields.emergency_procedures.fire_extinguishers}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text as="label" fontSize={16} pl="4px">
										Fire Extinguishers
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'emergency_procedures',
													field: 'cell_phone_2_way_radio_operational',
													value: !state.fields.emergency_procedures
														.cell_phone_2_way_radio_operational
												}
											});
										}}
										checked={state.fields.emergency_procedures.cell_phone_2_way_radio_operational}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text as="label" fontSize={16} pl="4px">
										Cell Phone/2-Way Radio Operational
									</Text>
								</HStack>
							</Flex>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'emergency_procedures',
													field: 'aed',
													value: !state.fields.emergency_procedures.aed
												}
											});
										}}
										checked={state.fields.emergency_procedures.aed}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text as="label" fontSize={16} pl="4px">
										AED
									</Text>
								</HStack>
							</Flex>
							<Flex>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text as="label" fontSize={16} pb="4px">
										Emergency Notes
									</Text>
									<Textarea
										fontSize={16}
										bgColor={theme.colors.white}
										value={state.fields.emergency_procedures.emergency_notes}
										onChange={({ target }) => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'emergency_procedures',
													field: 'emergency_notes',
													value: target.value
												}
											});
										}}
										disabled={isSubmitted && !edit}
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
									/>
								</Box>
							</Flex>
							{state.fields.emergency_procedures.emergency_contacts?.map((contact, index) => (
								<Flex key={index}>
									<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
										<Text as="label" fontSize={16} pb="4px">
											Name
										</Text>
										<Text fontSize={16}>{contact.emergency_contact_name}</Text>
									</Box>
									<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
										<Text as="label" fontSize={16} pb="4px">
											Phone Number
										</Text>
										<Text fontSize={16}>{contact.emergency_contact_phone_number}</Text>
									</Box>
								</Flex>
							))}
							{state.fields.emergency_procedures?.hospitals?.length ? (
								state.fields.emergency_procedures.hospitals.map(hospital => (
									<>
										<Flex>
											<Box
												w="100%"
												p="8px"
												m="8px"
												bgColor={theme.colors.gray[500]}
												borderRadius="base"
											>
												<Text as="label" fontSize={16} pb="4px">
													Hospital Name
												</Text>
												<Text fontSize={16}>{hospital.hospital_name}</Text>
											</Box>
											<Box
												w="100%"
												p="8px"
												m="8px"
												bgColor={theme.colors.gray[500]}
												borderRadius="base"
											>
												<Text as="label" fontSize={16} pb="4px">
													Hospital Phone Number
												</Text>
												<Text fontSize={16}>{hospital.hospital_phone_number}</Text>
											</Box>
										</Flex>
										<Flex>
											<Box
												w="100%"
												p="8px"
												m="8px"
												bgColor={theme.colors.gray[500]}
												borderRadius="base"
											>
												<Text as="label" fontSize={16} pb="4px">
													Hospital Location
												</Text>
												<Text fontSize={16}>{hospital.hospital_location}</Text>
											</Box>
										</Flex>
									</>
								))
							) : state.fields.emergency_procedures?.hospitals ? null : (
								<>
									<Flex>
										<Box
											w="100%"
											p="8px"
											m="8px"
											bgColor={theme.colors.gray[500]}
											borderRadius="base"
										>
											<Text as="label" fontSize={16} pb="4px">
												Hospital Name
											</Text>
											<Textarea
												fontSize={16}
												bgColor={theme.colors.white}
												value={state.fields.emergency_procedures.hospital_name}
												onChange={({ target }) => {
													dispatch({
														type: 'update_field_value',
														payload: {
															section: 'emergency_procedures',
															field: 'hospital_name',
															value: target.value
														}
													});
												}}
												disabled={isSubmitted && !edit}
												_disabled={{
													opacity: 1,
													bgColor: theme.colors.gray[400],
													color: theme.colors.black
												}}
											/>
										</Box>
										<Box
											w="100%"
											p="8px"
											m="8px"
											bgColor={theme.colors.gray[500]}
											borderRadius="base"
										>
											<Text as="label" fontSize={16} pb="4px">
												Hospital Phone Number
											</Text>
											<Textarea
												fontSize={16}
												bgColor={theme.colors.white}
												value={state.fields.emergency_procedures.hospital_phone_number}
												onChange={({ target }) => {
													dispatch({
														type: 'update_field_value',
														payload: {
															section: 'emergency_procedures',
															field: 'hospital_phone_number',
															value: target.value
														}
													});
												}}
												disabled={isSubmitted && !edit}
												_disabled={{
													opacity: 1,
													bgColor: theme.colors.gray[400],
													color: theme.colors.black
												}}
											/>
										</Box>
									</Flex>
									<Flex>
										<Box
											w="100%"
											p="8px"
											m="8px"
											bgColor={theme.colors.gray[500]}
											borderRadius="base"
										>
											<Text as="label" fontSize={16} pb="4px">
												Hospital Location
											</Text>
											<Input
												fontSize={16}
												bgColor={theme.colors.white}
												value={state.fields.emergency_procedures.hospital_location}
												onChange={({ target }) => {
													dispatch({
														type: 'update_field_value',
														payload: {
															section: 'emergency_procedures',
															field: 'hospital_location',
															value: target.value
														}
													});
												}}
												disabled={isSubmitted && !edit}
												_disabled={{
													opacity: 1,
													bgColor: theme.colors.gray[400],
													color: theme.colors.black
												}}
											/>
										</Box>
									</Flex>
								</>
							)}
							<Flex>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text as="label" fontSize={16} pb="4px">
										On-Call Dive Physician
									</Text>
									<Text fontSize={16}>HM Recompression</Text>
								</Box>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text as="label" fontSize={16} pb="4px">
										On-Call Dive Physician Phone Number
									</Text>
									<Text fontSize={16}>832-888-8909</Text>
								</Box>
							</Flex>
						</Accordion.ItemBody>
					</Accordion.ItemContent>
				</Accordion.Item>
				<Accordion.Item value={5} mb={4} borderBottomWidth="1px">
					<Accordion.ItemTrigger>
						<Accordion.ItemIndicator />
						<Text fontSize={20} fontWeight="bold" color={theme.colors.blue[700]}>
							<Text fontSize={20} fontWeight="bold" color={theme.colors.blue[700]}>
								Dive Meeting Attendees
							</Text>
						</Text>
					</Accordion.ItemTrigger>
					<Accordion.ItemContent p={0}>
						<Accordion.ItemBody>
							<Button
								size="lg"
								disabled={isSubmitted && !edit}
								onClick={() => dispatch({ type: 'add_meeting_attendee' })}
								color={theme.colors.white}
								bg={theme.colors.blue[700]}
								_hover={{ backgroundColor: theme.colors.blue[800] }}
							>
								Add Attendee
							</Button>
							{state.fields.crew?.map((cm, cmIndex) => (
								<Flex key={`${cm.name}_${cmIndex}`}>
									<Flex w="100%" alignItems="flex-start">
										<Box
											flex="2"
											p="8px"
											m="8px"
											bgColor={theme.colors.gray[500]}
											borderRadius="base"
										>
											<Text as="label" fontSize={16} pb="4px">
												Name
											</Text>
											<Text fontSize={16}>{cm.name}</Text>
										</Box>
										<Flex flex="1" flexDirection="column" minWidth="135px" maxWidth="135px">
											<Box
												flex="1"
												p="8px"
												m="8px"
												bgColor={theme.colors.gray[500]}
												borderRadius="base"
											>
												<Switch.Root
													colorPalette="blue"
													size="lg"
													pb="4px"
													onCheckedChange={() => {
														dispatch({
															type: 'update_crew_member',
															payload: {
																field: 'physically_fit',
																index: cmIndex,
																value: !cm.physically_fit
															}
														});
													}}
													checked={cm.physically_fit}
													disabled={isSubmitted && !edit}
												>
													<Switch.HiddenInput />
													<Switch.Control />
												</Switch.Root>
												<Text fontSize={16} as="label">
													Physically Fit
												</Text>
											</Box>
											<Box
												flex="1"
												p="8px"
												m="8px"
												bgColor={theme.colors.gray[500]}
												borderRadius="base"
											>
												<Switch.Root
													colorPalette="blue"
													size="lg"
													pb="4px"
													onCheckedChange={() => {
														dispatch({
															type: 'update_crew_member',
															payload: {
																field: 'ready_to_dive',
																index: cmIndex,
																value: !cm.ready_to_dive
															}
														});
													}}
													checked={cm.ready_to_dive}
													disabled={isSubmitted && !edit}
												>
													<Switch.HiddenInput />
													<Switch.Control />
												</Switch.Root>
												<Text fontSize={16} as="label" pl="4px">
													Ready to Dive
												</Text>
											</Box>
										</Flex>
									</Flex>
									<Flex
										w="100%"
										m="8px"
										borderBottomWidth="2px"
										borderBottomColor={cm.signature ? 'transparent' : 'red'}
										wrap="wrap"
										alignItems="flex-end"
									>
										<SignaturePad
											disabled={isSubmitted && !edit}
											src={
												cm.signature ||
												'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=='
											}
											onSign={signatureData =>
												dispatch({
													type: 'save_signature',
													payload: {
														name: cm.name,
														cmIndex,
														signatureData
													}
												})
											}
										/>
									</Flex>
									<Flex minWidth="55px" />
								</Flex>
							))}
							{state.fields.attendees?.map((cm, cmIndex) => (
								<Flex key={`${cmIndex}`}>
									<Flex w="100%" align="flex-start">
										<Flex flex="2" flexDirection="column">
											<Box
												flex="1"
												p="8px"
												m="8px"
												bgColor={theme.colors.gray[500]}
												borderRadius="base"
											>
												<Text as="label" fontSize={16} pb="4px">
													Name
												</Text>
												<Input
													fontSize={16}
													bgColor={theme.colors.white}
													value={cm.name}
													onChange={({ target }) => {
														dispatch({
															type: 'update_meeting_attendee',
															payload: {
																field: 'name',
																index: cmIndex,
																value: target.value
															}
														});
													}}
													disabled={isSubmitted && !edit}
													_disabled={{
														opacity: 1,
														bgColor: theme.colors.gray[400],
														color: theme.colors.black
													}}
												/>
											</Box>
											<Box
												flex="1"
												p="8px"
												m="8px"
												bgColor={theme.colors.gray[500]}
												borderRadius="base"
											>
												<Text as="label" fontSize={16} pb="4px">
													Company Name
												</Text>
												<Input
													fontSize={16}
													bgColor={theme.colors.white}
													value={cm.company_name}
													onChange={({ target }) => {
														dispatch({
															type: 'update_meeting_attendee',
															payload: {
																field: 'company_name',
																index: cmIndex,
																value: target.value
															}
														});
													}}
													disabled={isSubmitted && !edit}
													_disabled={{
														opacity: 1,
														bgColor: theme.colors.gray[400],
														color: theme.colors.black
													}}
												/>
											</Box>
										</Flex>
										<Flex flex="1" flexDirection="column" minWidth="135px" maxWidth="135px">
											<Box
												flex="1"
												p="8px"
												m="8px"
												bgColor={theme.colors.gray[500]}
												borderRadius="base"
											>
												<Switch.Root
													colorPalette="blue"
													size="lg"
													onCheckedChange={() => {
														dispatch({
															type: 'update_meeting_attendee',
															payload: {
																field: 'physically_fit',
																index: cmIndex,
																value: !cm.physically_fit
															}
														});
													}}
													checked={cm.physically_fit}
													disabled={isSubmitted && !edit}
												>
													<Switch.HiddenInput />
													<Switch.Control />
												</Switch.Root>
												<Text as="label" fontSize={16} pl="4px">
													Physically Fit
												</Text>
											</Box>
											<Box
												flex="1"
												p="8px"
												m="8px"
												bgColor={theme.colors.gray[500]}
												borderRadius="base"
											>
												<Switch.Root
													colorPalette="blue"
													size="lg"
													onCheckedChange={() => {
														dispatch({
															type: 'update_meeting_attendee',
															payload: {
																field: 'ready_to_dive',
																index: cmIndex,
																value: !cm.ready_to_dive
															}
														});
													}}
													checked={cm.ready_to_dive}
													disabled={isSubmitted && !edit}
												>
													<Switch.HiddenInput />
													<Switch.Control />
												</Switch.Root>
												<Text as="label" fontSize={16} pl="4px">
													Ready to Dive
												</Text>
											</Box>
										</Flex>
									</Flex>
									<Flex
										w="100%"
										m="8px"
										borderBottomWidth="2px"
										borderBottomColor={cm.signature ? 'transparent' : 'red'}
										wrap="wrap"
										alignItems="flex-end"
									>
										<SignaturePad
											disabled={isSubmitted && !edit}
											src={
												cm.signature ||
												'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=='
											}
											onSign={signatureData =>
												dispatch({
													type: 'save_signature',
													payload: {
														name: cm.name,
														cmIndex,
														signatureData,
														crew: false
													}
												})
											}
										/>
									</Flex>
									{window.isSupervisor && (!isSubmitted || edit) ? (
										<Flex alignItems="center">
											<CloseButton
												size="md"
												m="0 8px"
												height="40px"
												width="40px"
												color={theme.colors.white}
												bgColor={theme.colors.red[500]}
												_hover={{ background: theme.colors.red[600] }}
												disabled={isSubmitted && !edit}
												onClick={() => setRemoveMeetingAttendeeDialogOpen(cmIndex)}
											/>
											<Dialog.Root
												role="alertdialog"
												initialFocusEl={meetingAttendeeRefs.map.get(cmIndex)}
												onOpenChange={({ open }) => {
													if (!open) {
														setRemoveMeetingAttendeeDialogOpen(null);
													}
												}}
												open={removeMeetingAttendeeDialogOpen === cmIndex}
												placement="center"
											>
												<Portal>
													<Dialog.Backdrop />
													<Dialog.Positioner>
														<Dialog.Content opacity="1 !important">
															<Dialog.Header>
																<Dialog.Title fontSize="lg" fontWeight="bold">
																	Confirm Delete
																</Dialog.Title>
															</Dialog.Header>
															<Dialog.Body>
																<p>Are you sure you want to remove this item?</p>
															</Dialog.Body>
															<Dialog.Footer>
																<Dialog.ActionTrigger asChild>
																	<Button
																		size="lg"
																		ref={meetingAttendeeRefs.ref(cmIndex)}
																		onClick={() =>
																			setRemoveMeetingAttendeeDialogOpen(null)
																		}
																	>
																		Cancel
																	</Button>
																</Dialog.ActionTrigger>
																<Button
																	colorPalette="red"
																	size="lg"
																	ml={3}
																	bg={theme.colors.red[500]}
																	_hover={{ background: theme.colors.red[600] }}
																	color={theme.colors.white}
																	onClick={() => {
																		setRemoveMeetingAttendeeDialogOpen(null);
																		dispatch({
																			type: 'remove_meeting_attendee',
																			payload: { index: cmIndex }
																		});
																	}}
																>
																	Delete Item
																</Button>
															</Dialog.Footer>
															<Dialog.CloseTrigger asChild>
																<CloseButton size="sm" />
															</Dialog.CloseTrigger>
														</Dialog.Content>
													</Dialog.Positioner>
												</Portal>
											</Dialog.Root>
										</Flex>
									) : (
										<Flex minWidth="55px" />
									)}
								</Flex>
							))}
						</Accordion.ItemBody>
					</Accordion.ItemContent>
				</Accordion.Item>
			</Accordion.Root>
		</>
	);
};
